import type { BrandingFaviconBundle, BrandingFaviconLink } from '@app-types/branding.types.ts';

export const BRANDING_FAVICON_DATA_ATTRIBUTE = 'data-branding-favicon';

const CACHE_BUSTER_PARAM = 'v';

type MaybeBundle = BrandingFaviconBundle | null | undefined;

type MaybeLink = BrandingFaviconLink | null | undefined;

interface BrandingFaviconCacheHints {
  etag?: string | null | undefined;
  lastModified?: string | null | undefined;
  createdAt?: string | null | undefined;
  hint?: string | null | undefined;
}

function normalizeCacheHint(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function getCacheBusterKey(bundle: MaybeBundle, hints?: BrandingFaviconCacheHints | null): string | null {
  const candidates: Array<string | null | undefined> = [
    bundle?.etag,
    hints?.etag,
    hints?.lastModified,
    bundle?.created_at,
    hints?.createdAt,
    hints?.hint,
    bundle?.group_id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCacheHint(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

export function appendCacheBuster(href: string, cacheKey: string | null | undefined): string {
  if (!href) {
    return href;
  }
  if (!cacheKey) {
    return href;
  }
  const trimmedKey = cacheKey.trim();
  if (!trimmedKey) {
    return href;
  }

  const [withoutHash, hashFragment] = href.split('#', 2);
  const queryIndex = withoutHash.indexOf('?');

  if (queryIndex === -1) {
    const encoded = encodeURIComponent(trimmedKey);
    return `${withoutHash}?${CACHE_BUSTER_PARAM}=${encoded}${hashFragment ? `#${hashFragment}` : ''}`;
  }

  const base = withoutHash.slice(0, queryIndex);
  const query = withoutHash.slice(queryIndex + 1);
  const params = new URLSearchParams(query);
  params.set(CACHE_BUSTER_PARAM, trimmedKey);
  const serialized = params.toString();
  return `${base}?${serialized}${hashFragment ? `#${hashFragment}` : ''}`;
}

function normalizeLink(link: MaybeLink, cacheKey: string | null, seen: Set<string>, result: BrandingFaviconLink[]): void {
  if (!link || !link.href) {
    return;
  }
  const rel = link.rel?.trim();
  if (!rel) {
    return;
  }
  const href = appendCacheBuster(link.href, cacheKey);
  const type = link.type?.trim() ?? null;
  const sizes = link.sizes?.trim() ?? null;
  const color = link.color?.trim() ?? null;
  const dedupeKey = `${rel}|${href}|${sizes ?? ''}|${type ?? ''}|${color ?? ''}`;
  if (seen.has(dedupeKey)) {
    return;
  }
  seen.add(dedupeKey);
  result.push({ rel, href, type, sizes, color });
}

export function buildBrandingFaviconLinks(
  bundle: MaybeBundle,
  hints?: BrandingFaviconCacheHints | null,
): BrandingFaviconLink[] {
  if (!bundle) {
    return [];
  }
  const cacheKey = getCacheBusterKey(bundle, hints);
  const result: BrandingFaviconLink[] = [];
  const seen = new Set<string>();
  const links = Array.isArray(bundle.links) ? bundle.links : [];

  links.forEach((link) => normalizeLink(link, cacheKey, seen, result));

  if (result.length === 0 && bundle.default_href) {
    normalizeLink(
      {
        rel: 'icon',
        href: bundle.default_href,
        type: 'image/png',
        sizes: null,
        color: null,
      },
      cacheKey,
      seen,
      result,
    );
  }

  if (result.length === 0 && bundle.legacy_href) {
    normalizeLink(
      {
        rel: 'icon',
        href: bundle.legacy_href,
        type: 'image/x-icon',
        sizes: null,
        color: null,
      },
      cacheKey,
      seen,
      result,
    );
  }

  return result;
}

export function applyBrandingFaviconLinks(links: BrandingFaviconLink[], doc?: Document | null): void {
  const targetDocument = doc ?? (typeof document !== 'undefined' ? document : null);
  if (!targetDocument) {
    return;
  }
  const head = targetDocument.head;
  if (!head) {
    return;
  }

  const existing = head.querySelectorAll(`[${BRANDING_FAVICON_DATA_ATTRIBUTE}="true"]`);
  existing.forEach((node) => node.parentNode?.removeChild(node));

  if (!links || links.length === 0) {
    return;
  }

  links.forEach((linkConfig) => {
    if (!linkConfig.href) {
      return;
    }
    const element = targetDocument.createElement('link');
    element.setAttribute(BRANDING_FAVICON_DATA_ATTRIBUTE, 'true');
    element.rel = linkConfig.rel;
    element.href = linkConfig.href;
    if (linkConfig.type) {
      element.type = linkConfig.type;
    }
    if (linkConfig.sizes) {
      element.setAttribute('sizes', linkConfig.sizes);
    }
    if (linkConfig.color) {
      element.setAttribute('color', linkConfig.color);
    }
    head.appendChild(element);
  });
}

export interface SettingsWithBrandingFavicon {
  branding_favicon?: BrandingFaviconBundle | null;
  branding_favicon_cache_hint?: string | null;
  branding_favicon_updated_at?: string | null;
  etag?: string | null;
  lastModified?: string | null;
  last_modified?: string | null;
}

export function applyFaviconFromSettings(
  settings: SettingsWithBrandingFavicon | null | undefined,
  doc?: Document | null,
): void {
  const cacheHints: BrandingFaviconCacheHints = {
    etag: settings?.etag ?? null,
    lastModified: settings?.lastModified ?? settings?.last_modified ?? null,
    createdAt: settings?.branding_favicon_updated_at ?? null,
    hint: settings?.branding_favicon_cache_hint ?? null,
  };
  const links = buildBrandingFaviconLinks(settings?.branding_favicon ?? null, cacheHints);
  applyBrandingFaviconLinks(links, doc);
}
